(() => {
  'use strict';
  const configEl = document.getElementById('NiralaConfig');
  const config = configEl ? JSON.parse(configEl.textContent) : {root:'/', cart:'/cart'};
  const root = config.root.endsWith('/') ? config.root : config.root + '/';
  const controllers = new WeakMap();
  let refreshPromise = null;
  const drawer = () => document.querySelector('[data-cart-drawer]');
  const isEditor = () => Boolean(window.Shopify?.designMode || config.designMode);
  const tell = message => {const box=document.querySelector('[data-toast]');if(!box)return;box.textContent=message;box.hidden=false;clearTimeout(box._timer);box._timer=setTimeout(()=>box.hidden=true,5000);};
  let mutationTail = Promise.resolve();
  let mutationCount = 0;
  let checkoutPending = false;
  let cartEpoch = 0;
  let latestCart = null;
  function syncCartLoading() {
    document.documentElement.toggleAttribute('data-cart-loading', mutationCount > 0 || Boolean(refreshPromise));
  }
  function mutateCart(work) {
    mutationCount++;
    syncCartLoading();
    const operation = mutationTail.catch(() => {}).then(async () => {
      cartEpoch++;
      try { return await work(); }
      finally { cartEpoch++; }
    });
    mutationTail = operation.catch(() => {}).finally(() => {
      mutationCount--;
      syncCartLoading();
      document.dispatchEvent(new CustomEvent('nirala:cart-idle'));
    });
    return operation;
  }
  async function whenCartIdle() {
    let pending;
    do { pending = mutationTail; await pending; } while (pending !== mutationTail);
  }
  function publishCart(cart) {
    if (!Array.isArray(cart?.items) || !Number.isInteger(cart.item_count)) return;
    latestCart = cart;
    window.NiralaCartRenderer?.render(cart);
    syncMinimumOrder(cart);
    document.dispatchEvent(new CustomEvent('nirala:cart-updated', { detail: cart }));
  }
  async function readCart() {
    const response = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store', credentials: 'same-origin' });
    if (!response.ok) throw new Error('Your order could not be verified. Please try again.');
    const cart = await response.json();
    if (!Array.isArray(cart.items) || !Number.isInteger(cart.item_count)) throw new Error('Your order could not be verified. Please reload the cart.');
    return cart;
  }
  async function refreshCart({ fresh = false } = {}) {
    if (fresh && refreshPromise) await refreshPromise.catch(() => {});
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
      let epoch, cart;
      // A read that crossed another mutation cannot overwrite a newer view.
      do { epoch = cartEpoch; cart = await readCart(); } while (epoch !== cartEpoch);
      // Cart JSON is the complete source of truth. Section rendering is optional
      // on Shopify and must never gate a successful addition, change or removal.
      publishCart(cart);
      return cart;
    })().finally(() => { refreshPromise = null; syncCartLoading(); });
    syncCartLoading();
    return refreshPromise;
  }
  function openCart(){const el=drawer();if(el&&!el.open)el.showModal();}
  function restoreCartAction(form) {
    if (form.dataset.niralaCartAction) {
      form.setAttribute('action', form.dataset.niralaCartAction);
      delete form.dataset.niralaCartAction;
    }
    delete form.dataset.readySubmit;
  }
  function prepareCheckoutAddress(form) {
    const original = form.dataset.niralaCartAction || form.getAttribute('action') || config.cart || root + 'cart';
    const action = new URL(original, window.location.href);
    if (action.origin !== window.location.origin) throw new Error('Please reload your cart before checking out.');
    form.dataset.niralaCartAction = original;
    const address = window.NiralaLocation?.getCheckoutAddress?.();
    const fields = ['address1', 'address2', 'city', 'province', 'country', 'zip'];
    fields.forEach(field => {
      const key = `checkout[shipping_address][${field}]`;
      action.searchParams.delete(key);
      if (address && address[field]) action.searchParams.set(key, String(address[field]));
    });
    // Send address hints with the existing cart POST. Shopify controls checkout prefill.
    // Do not rebuild a variant permalink: that can lose item properties and selling plans.
    form.setAttribute('action', action.pathname + action.search);
  }
  function syncCartDeliverySummaries(attributes) {
    if (!attributes || typeof attributes !== 'object') return;
    const text = key => typeof attributes[key] === 'string' ? attributes[key].trim() : '';
    const pickup = text('Order type') === 'Pickup';
    const street = text('Delivery address');
    document.querySelectorAll('[data-cart-delivery-summary]').forEach(summary => {
      const title = summary.querySelector('[data-cart-delivery-title]');
      if (title) title.textContent = pickup ? 'Pickup address' : 'Delivery address';
      const fields = {
        '[data-cart-delivery-street]': street,
        '[data-cart-delivery-area]': pickup ? '' : text('Delivery area'),
        '[data-cart-delivery-city]': text('Delivery city')
      };
      Object.entries(fields).forEach(([selector, value]) => {
        const node = summary.querySelector(selector);
        if (node) { node.textContent = value; node.hidden = !value; }
      });
      const empty = summary.querySelector('[data-cart-delivery-empty]');
      if (empty) empty.hidden = Boolean(street);
    });
  }
  // The server-rendered cart already includes the saved address. Update the
  // visible summary after location changes and after quantity/note fragments
  // are replaced, without writing back to the customer's saved cart attributes.
  document.addEventListener('nirala:location-changed', event => {
    syncCartDeliverySummaries(event.detail?.attributes);
    document.querySelectorAll('[data-cart-form]').forEach(restoreCartAction);
  });
  document.addEventListener('nirala:cart-updated', event => {
    if (Number.isInteger(event.detail?.item_count)) document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = String(event.detail.item_count); });
    const saved = window.NiralaLocation?.getAttributes?.();
    syncCartDeliverySummaries(saved || event.detail?.attributes);
  });
  function formatMoney(amount, currency = config.currency) {
    const value = Number(amount);
    if (!Number.isFinite(value)) return '';
    currency = typeof currency === 'object' ? currency?.iso_code : currency;
    try {
      if ((currency || 'PKR') === 'PKR') return `Rs. ${new Intl.NumberFormat(config.locale || 'en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value / 100)}`;
      return new Intl.NumberFormat(config.locale || 'en-PK', {
        style: 'currency', currency: currency || 'PKR'
      }).format(value / 100);
    } catch (_) {
      return ((value / 100).toFixed(2)) + ' ' + (config.currency || 'PKR');
    }
  }
  function minimumOrderStatus(cart, form) {
    const summary = form?.querySelector('[data-cart-summary]');
    const minimum = Math.max(0, Math.round(Number(summary?.dataset.minimumOrderCents) || 0));
    // total_price includes product and cart discounts, and excludes delivery.
    const total = Number(cart?.total_price ?? summary?.dataset.cartTotalPrice);
    const enabled = summary?.dataset.minimumOrderEnabled === 'true' && minimum > 0;
    const blocked = enabled && (!Number.isFinite(total) || total < minimum);
    const remaining = Number.isFinite(total) ? Math.max(0, minimum - total) : minimum;
    const currency = cart?.currency || config.currency;
    return { blocked, minimum, remaining, message: blocked
      ? `Minimum order ${formatMoney(minimum, currency)}. Add ${formatMoney(remaining, currency)} more to continue.` : '' };
  }
  function syncMinimumOrder(cart, scope = document) {
    const forms = scope.matches?.('[data-cart-form]') ? [scope] : scope.querySelectorAll('[data-cart-form]');
    forms.forEach(form => {
      const summary = form.querySelector('[data-cart-summary]');
      if (summary && Number.isFinite(Number(cart?.total_price))) summary.dataset.cartTotalPrice = String(cart.total_price);
      const state = minimumOrderStatus(cart, form);
      const message = form.querySelector('[data-cart-minimum-message]');
      if (message) { message.textContent = state.message; message.hidden = !state.blocked; }
      const button = form.querySelector('[name="checkout"]');
      if (!button) return;
      const previouslyBlocked = button.dataset.minimumBlocked === 'true';
      button.hidden = state.blocked;
      if (state.blocked) {
        button.dataset.minimumBlocked = 'true';
        button.disabled = true;
      } else {
        delete button.dataset.minimumBlocked;
        if (previouslyBlocked && !button.dataset.addonBlocked && button.dataset.niralaBusy !== 'true'
          && form.dataset.checkoutPending !== 'true' && form.getAttribute('aria-busy') !== 'true'
          && form.dataset.cartControlsEmpty !== 'true') button.disabled = false;
      }
    });
  }
  window.Nirala = {
    config, refreshCart, readCart, publishCart, mutateCart, whenCartIdle, formatMoney, tell, minimumOrderStatus, syncMinimumOrder,
    isCartBusy: () => mutationCount > 0,
    getCart: () => latestCart,
    async addToCart(form, options = {}) {
      if (window.NiralaIncludedOptions && !window.NiralaIncludedOptions.validate(form)) return {cancelled:true};
      if (checkoutPending) throw new Error('Checkout is being prepared. Please wait.');
      if(window.NiralaStoreHours && !window.NiralaStoreHours.check()) return {cancelled:true, closed:true};
      if(window.NiralaLocation && !(await window.NiralaLocation.ensure())) return {cancelled:true};
      if(window.NiralaStoreHours && !window.NiralaStoreHours.check()) return {cancelled:true, closed:true};
      return mutateCart(async () => {
      if(window.NiralaStoreHours && !window.NiralaStoreHours.check()) return {cancelled:true, closed:true};
      const body = new FormData(form);
      body.delete('return_to');
      // An empty optional note is not a customization or a distinct cart line.
      for (const [name, value] of [...body.entries()]) {
        if (/^properties\[(?!_).+\]$/.test(name) && typeof value === 'string' && !value.trim()) body.delete(name);
      }
      const items = window.NiralaProductAddons?.prepareItems(form, body) || null;
      const headers = { Accept: 'application/json' };
      if (items) headers['Content-Type'] = 'application/json';
      let res;
      try {
        res = await fetch(root+'cart/add.js',{method:'POST',headers,body:items ? JSON.stringify({items}) : body});
      } catch (_) {
        throw new Error('We could not confirm the addition. Check your cart before trying again.');
      }
      let data;
      try { data = await res.json(); }
      catch (_) {
        if (!res.ok) throw new Error('We could not confirm the addition. Check your cart before trying again.');
        data = {};
      }
      if (!res.ok) {
        if (items) {
          try { await refreshCart({ fresh: true }); } catch (_) { /* Keep the original stock error. */ }
        }
        throw new Error((data.description || data.message || 'This item could not be added. Please try again.') + (items ? ' Review your cart before trying again.' : ''));
      }
      if (!data || typeof data !== 'object') data = {};
      if (items) {
        const added = Array.isArray(data.items) ? data.items : [];
        const parent = added.find(item => Number(item.variant_id || item.id) === Number(items[0].id) && !item.parent_relationship);
        const linked = parent?.key && items.slice(1).every(wanted => added.some(item =>
          Number(item.variant_id || item.id) === Number(wanted.id) && item.parent_relationship?.parent_key === parent.key
        ));
        if (!linked) data.addonReviewRequired = true;
      }
      try {
        // A header refresh may have started before the addition. Finish that
        // snapshot first, then request the current cart after this mutation.
        await refreshCart({ fresh: true });
        if (options.openCart !== false && config.ajaxCart) openCart();
        else if (!options.stayOnPage && !config.ajaxCart) window.location.assign(config.cart || root+'cart');
      } catch (error) {
        // The POST succeeded. Do not turn a rendering failure into an apparent
        // failed add that could make a customer order the same item twice.
        data.cartRefreshFailed = true;
        tell('Item added. Open your cart to review it.');
        if (!options.stayOnPage) window.location.assign(config.cart || root+'cart');
      }
      if (data.addonReviewRequired) {
        tell('Items added. Open your cart and check the topping quantities before checkout.');
        data.cartRefreshFailed = true; // Suppress a generic success message that would hide this review notice.
      }
      return data;
      });
    }
  };
  document.addEventListener('click',e=>{
    const menuLink=e.target.closest('[data-explore-menu]');
    const menuSection=document.querySelector('[data-menu-section]');
    if(menuLink&&menuSection){e.preventDefault();menuSection.scrollIntoView({behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth',block:'start'});}
    const cartLink=e.target.closest('[data-cart-open]') || e.target.closest('[data-sticky-cart]')?.querySelector('[data-cart-open]');
    if(cartLink&&config.ajaxCart&&drawer()){
      e.preventDefault();openCart();
      whenCartIdle().then(() => refreshCart({ fresh: true })).catch(error=>tell(error.message));
    }
    if(e.target.closest('[data-cart-close]'))drawer()?.close();
    if(e.target.matches('[data-cart-drawer]')){const r=e.target.getBoundingClientRect();if(e.clientX<r.left||e.clientX>r.right||e.clientY<r.top||e.clientY>r.bottom)e.target.close();}
  });
  document.addEventListener('submit', async event => {
    const submittedForm = event.target.closest('[data-cart-form]');
    if (!submittedForm || submittedForm.dataset.readySubmit) return;
    if (event.submitter?.name !== 'checkout') { restoreCartAction(submittedForm); return; }
    event.preventDefault();
    if (checkoutPending || (window.NiralaStoreHours && !window.NiralaStoreHours.check())) return;
    checkoutPending = true;
    const surface = submittedForm.closest('[data-cart-drawer]') ? '[data-cart-drawer]' : '.np-cart-page';
    const currentForm = () => document.querySelector(`${surface} [data-cart-form]`) || (submittedForm.isConnected ? submittedForm : null);
    let form = submittedForm;
    let button = event.submitter;
    let releaseButton = button ? window.NiralaBusyControls.lock(button, true) : () => {};
    const wasOpen = drawer()?.open;
    try {
      // Finish additions from any product surface, then save only deliberate edits.
      await whenCartIdle();
      form = currentForm();
      if (!form) throw new Error('Your cart is empty. Add an item before checkout.');
      if (window.NiralaCartControls && !(await window.NiralaCartControls.apply(form))) return;
      await whenCartIdle();
      form = currentForm();
      if (!form) throw new Error('Your cart is empty. Add an item before checkout.');
      const minimum = minimumOrderStatus(latestCart, form);
      if (minimum.blocked) throw new Error(minimum.message);
      const currentButton = form.querySelector('[name="checkout"]');
      if (currentButton !== button) {
        releaseButton();
        button = currentButton;
        releaseButton = button ? window.NiralaBusyControls.lock(button, true) : () => {};
      }
      form.dataset.checkoutPending = 'true';
      if (window.NiralaLocation && !window.NiralaLocation.isReady()) drawer()?.close();
      const ready = !window.NiralaLocation || await window.NiralaLocation.ensure();
      if (!ready) { if (wasOpen) openCart(); return; }
      if (window.NiralaStoreHours && !window.NiralaStoreHours.check()) { if (wasOpen) openCart(); return; }
      await whenCartIdle();
      await mutateCart(async () => {
        const expected = [...form.querySelectorAll('[data-cart-quantity]')].map(input => [input.dataset.cartKey, Number(input.dataset.cartOriginal)]).sort((a,b) => a[0].localeCompare(b[0]));
        const cart = await readCart();
        publishCart(cart);
        const actual = cart.items.map(item => [item.key, item.quantity]).sort((a,b) => a[0].localeCompare(b[0]));
        if (!cart.item_count || JSON.stringify(expected) !== JSON.stringify(actual)) {
          delete form.dataset.checkoutPending;
          await refreshCart({ fresh: true });
          throw new Error(cart.item_count ? 'Your cart changed. Please review the updated order and continue to checkout.' : 'Your cart is empty. Add an item before checkout.');
        }
        const minimum = minimumOrderStatus(cart, form);
        if (minimum.blocked) throw new Error(minimum.message);
        if (window.NiralaCartControls?.checkoutGuard()) throw new Error('Please review the item and topping quantities before checkout.');
        prepareCheckoutAddress(form);
        // Quantities/notes were already saved through exact-key Ajax mutations.
        // The checkout POST must never replay an old positional cart snapshot.
        const quantities = [...form.querySelectorAll('[data-cart-quantity]')].map(input => [input, input.disabled]);
        quantities.forEach(([input]) => { input.disabled = true; });
        try {
          form.dataset.readySubmit = 'true';
          button.disabled = false;
          form.requestSubmit(button);
        } finally {
          quantities.forEach(([input, disabled]) => { input.disabled = disabled; });
          delete form.dataset.readySubmit;
        }
      });
    } catch (error) {
      restoreCartAction(form || submittedForm);
      const message = currentForm()?.querySelector('[data-cart-error]');
      if (message) { message.textContent = error.message; message.hidden = false; }
      tell(error.message || 'Please review your cart before checkout.');
      if (wasOpen) openCart();
    } finally {
      checkoutPending = false;
      releaseButton();
      if (form) { delete form.dataset.checkoutPending; delete form.dataset.readySubmit; }
      window.NiralaCartControls?.init();
    }
  });
  window.addEventListener('pageshow', (event) => {
    document.querySelectorAll('[data-cart-form]').forEach(form => {
      restoreCartAction(form);
      const checkout = form.querySelector('[name="checkout"]');
      if (checkout) window.NiralaBusyControls.set(checkout, false, Boolean(checkout.dataset.addonBlocked || checkout.dataset.minimumBlocked || form.dataset.cartControlsEmpty === 'true'), false);
    });
    syncMinimumOrder(latestCart);
    if (event.persisted) whenCartIdle().then(() => refreshCart({ fresh: true })).catch(error => tell(error.message));
  });
  function initSlider(el){
    if(el.hasAttribute('data-hero-controller'))return;
    if(controllers.has(el))return;
    const abort=new AbortController(),{signal}=abort;
    const slides=[...el.querySelectorAll('[data-slide]')],dots=[...el.querySelectorAll('[data-slide-to]')];
    let index=0,timer=null,paused=false,hover=false;
    const reduce=window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    function schedule(){clearInterval(timer);if(el.dataset.autoplay==='true'&&slides.length>1&&!paused&&!hover&&!document.hidden&&!reduce&&!isEditor())timer=setInterval(()=>show(index+1),Number(el.dataset.interval)||6000);}
    function show(next){if(!slides.length)return;index=(next+slides.length)%slides.length;slides.forEach((slide,i)=>{slide.hidden=i!==index;slide.classList.toggle('is-active',i===index);});dots.forEach((dot,i)=>dot.setAttribute('aria-current',String(i===index)));schedule();}
    el.addEventListener('click',e=>{const dot=e.target.closest('[data-slide-to]');if(dot)show(Number(dot.dataset.slideTo));if(e.target.closest('[data-slide-prev]'))show(index-1);if(e.target.closest('[data-slide-next]'))show(index+1);const pause=e.target.closest('[data-slide-pause]');if(pause){paused=!paused;pause.textContent=paused?'Play':'Pause';pause.setAttribute('aria-label',paused?'Play slideshow':'Pause slideshow');schedule();}},{signal});
    el.addEventListener('mouseenter',()=>{hover=true;schedule();},{signal});el.addEventListener('mouseleave',()=>{hover=false;schedule();},{signal});
    el.addEventListener('focusin',()=>{hover=true;schedule();},{signal});el.addEventListener('focusout',e=>{if(!el.contains(e.relatedTarget)){hover=false;schedule();}},{signal});
    document.addEventListener('visibilitychange',schedule,{signal});
    el.addEventListener('shopify:block:select',e=>{const i=slides.findIndex(s=>s.dataset.blockId===e.detail.blockId);if(i>=0)show(i);},{signal});
    let start=null;el.addEventListener('touchstart',e=>start=e.changedTouches[0].clientX,{signal,passive:true});el.addEventListener('touchend',e=>{if(start!==null&&Math.abs(e.changedTouches[0].clientX-start)>70)show(index+(e.changedTouches[0].clientX<start?1:-1));start=null;},{signal,passive:true});
    controllers.set(el,()=>{clearInterval(timer);abort.abort();});show(0);
  }
  let headerObserver;
  function syncHeaderSize(){
    headerObserver?.disconnect();
    const header=document.querySelector('.np-header'),row=header?.querySelector('.np-header__row'),announcement=document.querySelector('.np-announcement');
    if(!header||!row)return;
    const measure=()=>{
      const height=row.getBoundingClientRect().height;
      if(height>0)document.documentElement.style.setProperty('--np-header-height',header.classList.contains('np-header--sticky')?height+'px':'0px');
      document.documentElement.style.setProperty('--np-announcement-height',(announcement?.getBoundingClientRect().height||0)+'px');
    };
    measure();
    if('ResizeObserver' in window){headerObserver=new ResizeObserver(measure);headerObserver.observe(row);if(announcement)headerObserver.observe(announcement);}
  }
  function mount(scope=document){scope.querySelectorAll('[data-slider]').forEach(initSlider);syncHeaderSize();}
  document.addEventListener('shopify:section:load',e=>mount(e.target));
  document.addEventListener('shopify:section:unload',e=>e.target.querySelectorAll('[data-slider]').forEach(el=>{controllers.get(el)?.();controllers.delete(el);}));
  // Menus work with native details and retain full links when JavaScript is unavailable.
  document.addEventListener('keydown',e=>{if(e.key==='Escape')document.querySelectorAll('.np-mobile-nav[open],.np-nav-dropdown[open]').forEach(el=>el.open=false);});
  mount();
  syncMinimumOrder(latestCart);
})();
